import { ChevronDown, Plus, Users, X } from 'lucide-react';
import PropTypes from 'prop-types';
import React from 'react';
import { createPortal } from 'react-dom';

import { getDashboardClients, getRoleById } from 'common-src/models/Client';

import useCustomSelector from 'src/hooks/useCustomSelector';

import {
  ESCALATED_TO_OPTIONS,
  PRIORITY_OPTIONS,
  ROLE_ID_TO_ESCALATION_TEAM,
  TASK_ASSIGNEE_PICKER_MODE,
  TASK_PRIORITY,
} from '../../constants';
import {
  getClientDisplayName,
  getNameInitials,
  getTaskDisplayTitle,
  getTaskDueDateInputValue,
  getTaskPriorityOrDefault,
  normalizeTeamValues,
} from '../../helpers';
import styles from './DelegateSubtasksModal.module.scss';

const createEmptySubtask = (task) => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  title: '',
  note: '',
  assignee: null,
  team: '',
  due: getTaskDueDateInputValue(null),
  priority: getTaskPriorityOrDefault(task, PRIORITY_OPTIONS, TASK_PRIORITY.High),
});

const ESCALATED_TO_TASK_TEAM = {
  [ESCALATED_TO_OPTIONS[0].value]: 'member',
  [ESCALATED_TO_OPTIONS[1].value]: 'clinical',
  [ESCALATED_TO_OPTIONS[2].value]: 'ops',
  [ESCALATED_TO_OPTIONS[3].value]: 'rcm',
};

const DelegateSubtasksModal = ({
  task,
  onClose,
  onConfirm,
  isSubmitting = false,
  currentUserName = '',
}) => {
  const clients = useCustomSelector((state) => getDashboardClients(state)) || [];
  const taskTitle = getTaskDisplayTitle(task);
  const [rows, setRows] = React.useState(() => [createEmptySubtask(task)]);
  const emptyTeamGroups = React.useMemo(
    () =>
      ESCALATED_TO_OPTIONS.reduce((acc, option) => {
        acc[option.value] = [];
        return acc;
      }, {}),
    [],
  );

  const { clientsByTeam, clientOptionsById } = React.useMemo(() => {
    const grouped = { ...emptyTeamGroups };
    const clientsById = {};

    clients.forEach((client) => {
      const label = getClientDisplayName(client);
      if (!client?.id || !label) return;

      const option = {
        id: client.id,
        label,
        role: client?.role || (client?.roleId ? getRoleById(client.roleId) : ''),
        initials: getNameInitials(label),
        teamValues: normalizeTeamValues(ROLE_ID_TO_ESCALATION_TEAM[client?.roleId]).map(
          (teamValue) => ESCALATED_TO_TASK_TEAM[teamValue],
        ),
      };
      clientsById[client.id] = option;

      normalizeTeamValues(ROLE_ID_TO_ESCALATION_TEAM[client?.roleId]).forEach((teamValue) => {
        grouped[teamValue].push(option);
      });
    });

    Object.keys(grouped).forEach((teamValue) => {
      grouped[teamValue].sort((a, b) => a.label.localeCompare(b.label));
    });

    return { clientsByTeam: grouped, clientOptionsById: clientsById };
  }, [clients, emptyTeamGroups]);

  const updateRow = (rowId, patch) => {
    setRows((currentRows) =>
      currentRows.map((row) => (row.id === rowId ? { ...row, ...patch } : row)),
    );
  };

  const addRow = () => {
    setRows((currentRows) => [...currentRows, createEmptySubtask(task)]);
  };

  const removeRow = (rowId) => {
    setRows((currentRows) => currentRows.filter((row) => row.id !== rowId));
  };

  const allValid = rows.length > 0 && rows.every((row) => row.title.trim().length > 0 && row.team);

  const modalContent = (
    <div
      className={styles.modalOverlay}
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className={styles.modalCard}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delegate-subtasks-title"
      >
        <div className={styles.modalHeader}>
          <div id="delegate-subtasks-title" className={styles.modalTitle}>
            Delegate sub-tasks
          </div>
          <div className={styles.modalSubtitle}>
            Create sub-tasks that must finish before{' '}
            <span className={styles.modalSubtitleStrong}>{taskTitle}</span>. Assign to a person or
            route to a team to claim.
          </div>
        </div>

        <div className={styles.modalBody}>
          {rows.map((row, index) => (
            <SubtaskRow
              key={row.id}
              index={index}
              row={row}
              canRemove={rows.length > 1}
              clientOptionsById={clientOptionsById}
              currentUserName={currentUserName}
              clientsByTeam={clientsByTeam}
              onChange={(patch) => updateRow(row.id, patch)}
              onRemove={() => removeRow(row.id)}
            />
          ))}

          <button type="button" onClick={addRow} className={styles.addSubtaskButton}>
            <Plus size={14} />
            Add another sub-task
          </button>
        </div>

        <div className={styles.modalFooter}>
          <button type="button" onClick={onClose} className={styles.modalSecondaryAction}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.modalPrimaryAction}
            disabled={!allValid || isSubmitting}
            onClick={async () => {
              if (!allValid) return;
              const shouldClose = await onConfirm?.(rows);
              if (shouldClose !== false) onClose();
            }}
          >
            {isSubmitting
              ? 'Creating...'
              : `Create ${rows.length} sub-task${rows.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return modalContent;

  return createPortal(modalContent, document.body);
};

const SubtaskRow = ({
  index,
  row,
  canRemove,
  clientOptionsById,
  currentUserName,
  clientsByTeam,
  onChange,
  onRemove,
}) => {
  const [isPickerOpen, setIsPickerOpen] = React.useState(false);
  const [pickerMode, setPickerMode] = React.useState(TASK_ASSIGNEE_PICKER_MODE.Team);
  const [query, setQuery] = React.useState('');
  const [openTeams, setOpenTeams] = React.useState(() =>
    ESCALATED_TO_OPTIONS.reduce((acc, option, optionIndex) => {
      acc[option.value] = optionIndex === 0;
      return acc;
    }, {}),
  );
  const pickerRef = React.useRef(null);
  const popoverRef = React.useRef(null);
  const [popoverStyle, setPopoverStyle] = React.useState(null);

  const updatePopoverPosition = React.useCallback(() => {
    const triggerElement = pickerRef.current;
    if (!triggerElement) return;

    const rect = triggerElement.getBoundingClientRect();
    const viewportPadding = 16;
    const popoverWidth = Math.min(360, window.innerWidth - viewportPadding * 2);
    const left = Math.min(rect.left, window.innerWidth - popoverWidth - viewportPadding);

    setPopoverStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: Math.max(viewportPadding, left),
      width: popoverWidth,
    });
  }, []);

  React.useEffect(() => {
    if (!isPickerOpen) return undefined;

    updatePopoverPosition();

    const handlePointerDown = (event) => {
      if (pickerRef.current?.contains(event.target)) return;
      if (popoverRef.current?.contains(event.target)) return;
      setIsPickerOpen(false);
    };

    const handleViewportChange = () => updatePopoverPosition();

    document.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [isPickerOpen, updatePopoverPosition]);

  const filteredClientsByTeam = React.useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return clientsByTeam;

    const grouped = {};
    Object.entries(clientsByTeam).forEach(([teamValue, teamClients]) => {
      grouped[teamValue] = teamClients.filter((item) => {
        const haystack = `${item.label} ${item.role}`.toLowerCase();
        return haystack.includes(normalizedQuery);
      });
    });

    return grouped;
  }, [clientsByTeam, query]);

  const selectedUser =
    row.assignee?.type === 'user' ? (clientOptionsById[row.assignee.userId] ?? null) : null;
  const selectedTeam =
    row.assignee?.type === 'team'
      ? ESCALATED_TO_OPTIONS.find((option) => option.value === row.assignee.team)
      : null;

  const toggleTeam = (teamValue) => {
    setOpenTeams((currentOpenTeams) => ({
      ...currentOpenTeams,
      [teamValue]: !currentOpenTeams[teamValue],
    }));
  };

  return (
    <div className={styles.subtaskCard}>
      <div className={styles.subtaskCardHeader}>
        <span className={styles.subtaskLabel}>{`Sub-task ${index + 1}`}</span>
        {canRemove ? (
          <button
            type="button"
            onClick={onRemove}
            className={styles.removeSubtaskButton}
            aria-label="Remove sub-task"
          >
            <X size={14} />
          </button>
        ) : null}
      </div>

      <input
        type="text"
        value={row.title}
        onChange={(event) => onChange({ title: event.target.value })}
        className={styles.textInput}
        placeholder="Sub-task title — what needs to happen?"
      />

      <textarea
        value={row.note}
        onChange={(event) => onChange({ note: event.target.value })}
        className={styles.textarea}
        placeholder="Add a message for the assignee — this becomes the task description."
        rows={2}
      />

      <div className={styles.subtaskControlsRow}>
        <div className={styles.assigneePickerWrap} ref={pickerRef}>
          <button
            type="button"
            className={`${styles.assigneeTrigger} ${row.assignee ? styles.assigneeTriggerActive : ''}`}
            onClick={() => setIsPickerOpen((current) => !current)}
          >
            {selectedUser ? (
              <>
                <span className={styles.userAvatar}>{selectedUser.initials}</span>
                <span className={styles.assigneePrimary}>{selectedUser.label}</span>
                <span className={styles.assigneeSecondary}>
                  {selectedUser.role || 'Unassigned'}
                </span>
              </>
            ) : selectedTeam ? (
              <>
                <span className={styles.teamAvatar}>{selectedTeam.icon}</span>
                <span className={styles.assigneePrimary}>{selectedTeam.label} team</span>
                <span className={styles.assigneeSecondary}>any member</span>
              </>
            ) : (
              <>
                <span className={styles.assigneePlaceholder}>Pick assignee or team</span>
                <ChevronDown size={12} />
              </>
            )}
          </button>

          {isPickerOpen && popoverStyle
            ? createPortal(
                <div className={styles.assigneePopover} style={popoverStyle} ref={popoverRef}>
                  <div className={styles.assigneePopoverTabs}>
                    <button
                      type="button"
                      className={`${styles.assigneePopoverTab} ${
                        pickerMode === TASK_ASSIGNEE_PICKER_MODE.Team
                          ? styles.assigneePopoverTabActive
                          : ''
                      }`}
                      onClick={() => setPickerMode(TASK_ASSIGNEE_PICKER_MODE.Team)}
                    >
                      <Users size={13} />
                      Team
                    </button>
                    <button
                      type="button"
                      className={`${styles.assigneePopoverTab} ${
                        pickerMode === TASK_ASSIGNEE_PICKER_MODE.User
                          ? styles.assigneePopoverTabActive
                          : ''
                      }`}
                      onClick={() => setPickerMode(TASK_ASSIGNEE_PICKER_MODE.User)}
                    >
                      Person
                    </button>
                  </div>

                  {pickerMode === TASK_ASSIGNEE_PICKER_MODE.Team ? (
                    <div className={styles.teamPickerBody}>
                      <div className={styles.teamPickerHint}>
                        Any member of the selected team can pick up the sub-task.
                      </div>
                      <div className={styles.teamPickerGrid}>
                        {ESCALATED_TO_OPTIONS.map((option) => {
                          const isSelected = selectedTeam?.value === option.value;
                          return (
                            <button
                              key={option.value}
                              type="button"
                              className={`${styles.teamPickerCard} ${
                                isSelected ? styles.teamPickerCardActive : ''
                              }`}
                              onClick={() => {
                                onChange({
                                  assignee: { type: 'team', team: option.value },
                                  team: ESCALATED_TO_TASK_TEAM[option.value] || '',
                                });
                                setIsPickerOpen(false);
                              }}
                            >
                              <span className={styles.teamPickerIcon}>{option.icon}</span>
                              <span className={styles.teamPickerLabel}>{option.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className={styles.userPickerBody}>
                      <div className={styles.userSearchWrap}>
                        <input
                          type="text"
                          value={query}
                          onChange={(event) => setQuery(event.target.value)}
                          className={styles.userSearchInput}
                          placeholder="Search people..."
                        />
                      </div>

                      <div className={styles.userPickerList}>
                        {query.trim().length > 0
                          ? Object.entries(filteredClientsByTeam).flatMap(
                              ([teamValue, teamClients]) =>
                                teamClients.map((item) => {
                                  const isSelected = selectedUser?.id === item.id;
                                  const teamLabel =
                                    ESCALATED_TO_OPTIONS.find(
                                      (option) => `${option.value}` === teamValue,
                                    )?.label || '';
                                  const isYou =
                                    !!currentUserName &&
                                    item.label.toLowerCase() ===
                                      currentUserName.trim().toLowerCase();

                                  return (
                                    <button
                                      key={`${teamValue}-${item.id}`}
                                      type="button"
                                      className={`${styles.userPickerRow} ${
                                        isSelected ? styles.userPickerRowActive : ''
                                      }`}
                                      onClick={() => {
                                        onChange({
                                          assignee: { type: 'user', userId: item.id },
                                          team: item.teamValues[0] || '',
                                        });
                                        setIsPickerOpen(false);
                                      }}
                                    >
                                      <span className={styles.userPickerAvatar}>
                                        {item.initials}
                                      </span>
                                      <span className={styles.userPickerInfo}>
                                        <span className={styles.userPickerNameRow}>
                                          <span className={styles.userPickerName}>
                                            {item.label}
                                          </span>
                                          {isYou ? (
                                            <span className={styles.userPickerYou}>(you)</span>
                                          ) : null}
                                        </span>
                                        <span className={styles.userPickerRole}>
                                          {item.role || '—'}
                                        </span>
                                      </span>
                                      <span className={styles.userPickerBadge}>{teamLabel}</span>
                                    </button>
                                  );
                                }),
                            )
                          : ESCALATED_TO_OPTIONS.map((option) => {
                              const teamClients = filteredClientsByTeam[option.value] || [];
                              const isOpen = !!openTeams[option.value];

                              if (!teamClients.length) return null;

                              return (
                                <div key={option.value} className={styles.userTeamSection}>
                                  <button
                                    type="button"
                                    className={styles.userTeamHeader}
                                    onClick={() => toggleTeam(option.value)}
                                  >
                                    <span className={styles.userTeamHeaderLeft}>
                                      <span
                                        className={`${styles.userTeamChevron} ${
                                          isOpen ? styles.userTeamChevronOpen : ''
                                        }`}
                                      >
                                        ›
                                      </span>
                                      <span className={styles.userTeamIcon}>{option.icon}</span>
                                      <span className={styles.userTeamTitle}>{option.label}</span>
                                    </span>
                                    <span className={styles.userTeamCount}>
                                      {teamClients.length}
                                    </span>
                                  </button>

                                  {isOpen ? (
                                    <div className={styles.userTeamList}>
                                      {teamClients.map((item) => {
                                        const isSelected = selectedUser?.id === item.id;
                                        const isYou =
                                          !!currentUserName &&
                                          item.label.toLowerCase() ===
                                            currentUserName.trim().toLowerCase();

                                        return (
                                          <button
                                            key={item.id}
                                            type="button"
                                            className={`${styles.userPickerRow} ${
                                              isSelected ? styles.userPickerRowActive : ''
                                            }`}
                                            onClick={() => {
                                              onChange({
                                                assignee: { type: 'user', userId: item.id },
                                                team: item.teamValues[0] || '',
                                              });
                                              setIsPickerOpen(false);
                                            }}
                                          >
                                            <span className={styles.userPickerAvatar}>
                                              {item.initials}
                                            </span>
                                            <span className={styles.userPickerInfo}>
                                              <span className={styles.userPickerNameRow}>
                                                <span className={styles.userPickerName}>
                                                  {item.label}
                                                </span>
                                                {isYou ? (
                                                  <span className={styles.userPickerYou}>
                                                    (you)
                                                  </span>
                                                ) : null}
                                              </span>
                                              <span className={styles.userPickerRole}>
                                                {item.role || '—'}
                                              </span>
                                            </span>
                                            <span className={styles.userPickerBadge}>
                                              {option.label}
                                            </span>
                                          </button>
                                        );
                                      })}
                                    </div>
                                  ) : null}
                                </div>
                              );
                            })}
                      </div>
                    </div>
                  )}
                </div>,
                document.body,
              )
            : null}
        </div>

        <input
          type="date"
          value={row.due}
          min={getTaskDueDateInputValue(null)}
          onChange={(event) => onChange({ due: event.target.value })}
          className={styles.dateInput}
        />

        <select
          value={row.priority}
          onChange={(event) => onChange({ priority: event.target.value })}
          className={styles.prioritySelect}
        >
          {PRIORITY_OPTIONS.map((priority) => (
            <option key={priority} value={priority}>
              {priority}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

DelegateSubtasksModal.propTypes = {
  task: PropTypes.shape({
    title: PropTypes.string,
    assessmentType: PropTypes.string,
    assessmentVariant: PropTypes.string,
    due: PropTypes.string,
    priority: PropTypes.string,
  }),
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  isSubmitting: PropTypes.bool,
  currentUserName: PropTypes.string,
};

SubtaskRow.propTypes = {
  index: PropTypes.number,
  row: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    note: PropTypes.string,
    due: PropTypes.string,
    priority: PropTypes.string,
    assignee: PropTypes.shape({
      type: PropTypes.oneOf(['team', 'user']),
      team: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      userId: PropTypes.number,
    }),
    team: PropTypes.string,
  }),
  canRemove: PropTypes.bool,
  clientOptionsById: PropTypes.object,
  currentUserName: PropTypes.string,
  clientsByTeam: PropTypes.object,
  onChange: PropTypes.func,
  onRemove: PropTypes.func,
};

export default DelegateSubtasksModal;
